import React, { useCallback, useContext } from 'react';

import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import usePressTracing from '@atlaskit/react-ufo/use-press-tracing';
import UFOLabel from '@jira/platform__app-framework-ufo-label/src/index.tsx';
import { SECTION_ITEM_MORE } from '@jira/navigation-apps__sidebar-nav4-analytics/src/common/constants/analytics/section-type.tsx';
import {
	NavItemsContext,
	PeekMenuIdContext,
} from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/controllers/context.tsx';
import type { JiraConfigurableNavigationItem } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/types.tsx';
import { useSectionItemAnalytics } from '@jira/navigation-apps__sidebar-nav4-sidebars-common-core/src/common/utils/use-section-item-analytics/index.tsx';
import { GlobalMoreMenu } from '@atlassian/global-more-menu/ui';
import type { CustomizableMenuItemData } from '@atlassian/customization-modal/types';
import { fg } from '@atlassian/jira-feature-gating';
import type { ConfigurableMenuItem$key } from '../../../common/ui/configurable-menu-item/__generated__/ConfigurableMenuItem.graphql';
import { MoreMenuItem } from './more-menu-item/index.tsx';
import { PeekMenuItem } from './peek-menu-item/index.tsx';
import { CustomizeSidebar } from './customize-sidebar/index.tsx';

export type MoreMenuItemProps = {
	navItems: JiraConfigurableNavigationItem[];
	menuData: ConfigurableMenuItem$key;
	productItemsData: CustomizableMenuItemData[];
	appItemsData: CustomizableMenuItemData[];
	getAppShortcutURL?: (apsKey: string) => string | undefined;
};

export function SidebarCustomization({
	menuData,
	productItemsData,
	appItemsData,
	getAppShortcutURL,
}: MoreMenuItemProps): React.JSX.Element {
	const { moreMenuIds } = useContext(NavItemsContext);
	const { peekMenuId } = useContext(PeekMenuIdContext);
	const sendSectionItemAnalyticsEvent = useSectionItemAnalytics();
	const pressTracing = usePressTracing('nav4-sidebar-more');

	const onClickMoreMenuTrigger = useCallback(() => {
		sendSectionItemAnalyticsEvent({ sectionItem: SECTION_ITEM_MORE });
		pressTracing();
	}, [pressTracing, sendSectionItemAnalyticsEvent]);

	if (moreMenuIds.length === 0) {
		return <CustomizeSidebar productItemsData={productItemsData} appItemsData={appItemsData} />;
	}

	const moreMenu = (
		<GlobalMoreMenu
			productItemsData={productItemsData}
			appItemsData={appItemsData}
			menuItemComponent={(props) => (
				<MoreMenuItem {...props} getAppShortcutURL={getAppShortcutURL} />
			)}
			customizeSidebarMenuItem={
				<CustomizeSidebar productItemsData={productItemsData} appItemsData={appItemsData} />
			}
			peekingMenuIds={peekMenuId ? [peekMenuId] : undefined}
			onClickMoreMenuTrigger={onClickMoreMenuTrigger}
		/>
	);

	return (
		<UFOLabel name="nav4-sidebar-customization">
			<JSErrorBoundary
				fallback="flag"
				id="sidebar-customization"
				packageName="jiraNavigationAppsSidebarNav4SidebarsContentCore"
				teamName="Navigation"
			>
				<PeekMenuItem menuData={menuData} />

				<div
					role="listitem"
					id={fg('ops-overview-disc-spotlights') ? 'more-button-menu-item' : undefined}
				>
					{moreMenu}
				</div>
			</JSErrorBoundary>
		</UFOLabel>
	);
}
